// src/api/MenuItemApi.tsx

import axios from "axios";
import { Constants } from "../constants/constants";
import { ResponseMessage } from "../models/ResponseMessage";

const API_BASE_URL = Constants.API_BASE_URL;

export interface MenuItem {
  id: string;
  restaurantId: string;
  categoryId?: string | null;

  name: string;
  description?: string | null;

  price: number;
  imageUrl?: string | null;

  isAvailable: boolean;
  dietaryTags?: string | null;
  calories?: number | null;

  displayOrder: number;

  createdAt: string; // ISO string from backend
  updatedAt: string; // ISO string from backend
}

// ================= DTO TYPES =================

export interface MenuItemDto {
  id: string;
  name: string;
  description?: string;
  price: number;
  imageUrl?: string;
  isAvailable: boolean;
  dietaryTags?: string;
  calories?: number;
  displayOrder: number;
  categoryId: string;
  createdAt: string; // ISO string from backend
}

export interface MenuItemCreateDto {
  categoryId: string;
  name: string;
  description?: string;
  price: number;
  imageUrl?: string;
  isAvailable?: boolean;
  dietaryTags?: string;
  calories?: number;
  displayOrder?: number;
}

export interface MenuItemUpdateDto {
  categoryId?: string;
  name?: string;
  description?: string;
  price?: number;
  imageUrl?: string;
  isAvailable?: boolean;
  dietaryTags?: string;
  calories?: number;
  displayOrder?: number;
}

export interface ToggleAvailabilityDto {
  isAvailable: boolean;
}

// ================= AXIOS SETUP =================

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Attach token
api.interceptors.request.use((config) => {
  const raw = localStorage.getItem(Constants.ACCESS_TOKEN);

  if (raw) {
    try {
      config.headers.Authorization = `Bearer ${raw}`;
    } catch (err) {
      console.error("Invalid token in storage");
    }
  }

  return config;
});

// ================= API METHODS =================

// 🔹 GET all menu items (with optional category filter)
export const getMenuItems = async (
  restaurantId: string,
  categoryId?: string,
): Promise<ResponseMessage<MenuItem[]>> => {
  const res = await api.get<ResponseMessage<MenuItem[]>>(`/menuitems/restaurants/${restaurantId}`,
    {
      params: categoryId ? { categoryId } : {},
    },
  );

  return res.data;
};

// 🔹 GET available menu items only
export const getAvailableMenuItems = async (
  restaurantId: string,
  categoryId?: string,
): Promise<ResponseMessage<MenuItemDto[]>> => {
  const res = await api.get<ResponseMessage<MenuItemDto[]>>(
    `/menuitems/restaurants/${restaurantId}/available`,
    {
      params: categoryId ? { categoryId } : {},
    },
  );

  return res.data;
};

// 🔹 GET single item
export const getMenuItemById = async (itemId: string): Promise<MenuItemDto> => {
  const res = await api.get<MenuItemDto>(`/MenuItem/${itemId}`);
  return res.data;
};

// 🔹 CREATE menu item
export const createMenuItem = async (restaurantId: string, data: any) => {
  const formData = new FormData();

  formData.append("name", data.name);
  formData.append("price", data.price.toString());
  formData.append("categoryId", data.categoryId);

  if (data.description) formData.append("description", data.description);

  if (data.calories) formData.append("calories", data.calories.toString());

  if (data.dietaryTags) formData.append("dietaryTags", data.dietaryTags);

  formData.append("displayOrder", (data.displayOrder || 0).toString());
  formData.append("isAvailable", data.isAvailable ? "true" : "false");

  if (data.imageFile) {
    formData.append("imageFile", data.imageFile);
  }

  const res = await api.post(
    `/MenuItems/restaurants/${restaurantId}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return res.data;
};

// 🔹 UPDATE menu item
export const updateMenuItem = async (itemId: string,restaurantId: string,data: MenuItemUpdateDto,
): Promise<ResponseMessage<MenuItem>> => {
  const res = await api.put<ResponseMessage<MenuItem>>(
    `/menuitems/${itemId}/restaurants/${restaurantId}`,
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return res.data;
};

// 🔹 TOGGLE availability
export const toggleMenuItemAvailability = async (
  itemId: string,
  isAvailable: boolean,
): Promise<boolean> => {
  const payload: ToggleAvailabilityDto = { isAvailable };

  const res = await api.patch<boolean>(
    `/menuitems/${itemId}/availability`,
    payload,
  );

  return res.data;
};

// 🔹 DELETE menu item
export const deleteMenuItem = async (itemId: string): Promise<boolean> => {
  const res = await api.delete<boolean>(`/MenuItem/${itemId}`);
  return res.data;
};
